import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import { FormattedHTMLMessage } from '@bibliocommons/react-intl';
import { TransactionalSolidButton } from '@bibliocommons/base-buttons';
import { Link } from '@bibliocommons/base-links';
import RadioButton from '@bibliocommons/base-radio-button';
import TextField from '@bibliocommons/text-field';
import { Select, SelectOption } from '@bibliocommons/select';
import { ModalHeader, ModalContent, ModalFooter } from '@bibliocommons/modal';
import { libraryShape, userIdentityShape } from '@bibliocommons/bc-prop-types';
import { eventShape } from 'app/shapes/events';
import ModalActions from 'app/actions/ModalActions';
import EventsActions from 'app/actions/EventsActions';
import EventDateStamp from 'app/components/events/EventDateStamp';
import EventDateTime from 'app/components/shared/EventDateTime';

import './EventRegistrationModal.scss';

const ERROR_CONFLICT = 'Error: Conflict';
const CONTACT_PREFERENCES = {
  EMAIL: 'email',
  PHONE: 'phone'
};

const CONTACT_LABEL = {
  EMAIL: 'email_address',
  PHONE: 'telephone_number'
};

const WAITLIST = 'waitlist';

export default function EventRegistrationModal({
  currentLibrary,
  currentUser,
  event,
  eventSeries,
  modalKey,
  status,
  eventSettings
}) {
  const dispatch = useDispatch();
  const [firstName, setFirstName] = useState(currentUser?.getIn(['userContact', 'firstName']) ?? '');
  const [lastName, setLastName] = useState(currentUser?.getIn(['userContact', 'lastName']) ?? '');
  const [contactValue, setContactValue] = useState(currentUser?.getIn(['userContact', 'email']) ?? '');
  const [contactType, setContactType] = useState(CONTACT_PREFERENCES.EMAIL);
  const [resendEmail, setResendEmail] = useState(false);

  const fieldLabel = contactType === CONTACT_PREFERENCES.PHONE ? CONTACT_LABEL.PHONE : CONTACT_LABEL.EMAIL;
  const registrationInfo = event.getIn(['definition', 'registrationInfo']);
  const scope = registrationInfo.get('scope');
  const firstEventKey = eventSeries.getIn(['eventKeys', 0]).toString();
  const [date, time] = firstEventKey?.split('T'); // eslint-disable-line no-unused-vars
  const seriesStartDate = scope && moment(date).format('LL');
  const seriesStartDay = scope && moment(eventSeries.getIn(['eventKeys', 0])).format('dddd');
  const seatsRemaining = registrationInfo.get('cap') - event.get('numberRegistered');
  const maxSeats =
    Math.min(
      seatsRemaining,
      registrationInfo.get('cap'),
      registrationInfo.get('maxSeats'),
      eventSettings?.getIn(['eventRegistrationOptions', 'maxSeats'])
    ) || 1;
  // Convert maxSeats to array of numbers from 1 to maxSeats
  const attendanceOptions = Array.from({ length: maxSeats }, (_, i) => i + 1);
  const [attendance, setAttendance] = useState(attendanceOptions[0]);

  function handleClose() {
    dispatch(ModalActions.closeModal(modalKey));
  }

  function handleEventRegistration() {
    const query = {
      eventId: event.get('id'),
      seriesId: event.get('seriesId'),
      userFirstName: firstName,
      userLastName: lastName,
      contactType,
      contactValue,
      numberAttending: attendance,
      scope,
      status
    };
    dispatch(
      EventsActions.createRegistration({
        query,
        postSuccess: () => {
          handleClose();
        },
        postFailure: err => {
          if (err?.message === ERROR_CONFLICT) {
            setResendEmail(true);
          } else {
            handleClose();
          }
        }
      })
    );
  }

  function handleResendConfirmationEmail() {
    dispatch(
      EventsActions.resendConfirmationEmail({
        email: contactValue,
        eventId: event.get('id')
      })
    );
    handleClose();
  }

  function handleFilter(type) {
    setContactType(type);
    if (type === CONTACT_PREFERENCES.EMAIL) {
      setContactValue(currentUser?.getIn(['userContact', 'email']));
    } else {
      setContactValue('');
    }
  }

  function renderLabel() {
    if (status === WAITLIST) {
      return <FormattedMessage defaultMessage="Join waitlist" id="join_waitlist" />;
    }
    return <FormattedMessage defaultMessage="Register" id="register" />;
  }

  function renderEventInfo() {
    if (scope) {
      return (
        <FormattedMessage
          id="events.registration.series.dates.message"
          values={{ seriesStartDay, seriesStartDate }}
          defaultMessage={`Series begins ${seriesStartDay}, ${seriesStartDate}.`}
        />
      );
    }
    return (
      <EventDateTime
        start={event.getIn(['definition', 'start'])}
        end={event.getIn(['definition', 'end'])}
        includeYear
      />
    );
  }

  function renderContact() {
    const emailRequiredForWaitlist = eventSettings?.getIn(['eventRegistrationOptions', 'emailRequiredForWaitlist']);
    const emailRequired = !emailRequiredForWaitlist && !currentUser;

    if (emailRequired) {
      return (
        <TextField
          className="contact"
          label={<FormattedMessage id={CONTACT_LABEL.EMAIL} defaultMessage="Email" />}
          id="reg_contact_value"
          size="large"
          type={contactType === CONTACT_PREFERENCES.EMAIL ? CONTACT_PREFERENCES.EMAIL : 'text'}
          value={contactValue}
          onChange={setContactValue}
        />
      );
    }

    return (
      <div className="registration-form-contact">
        <div className="section-title">
          <FormattedMessage id="contact_preference" defaultMessage="Contact preference" />
        </div>
        <ul>
          <li key={CONTACT_PREFERENCES.EMAIL}>
            <RadioButton
              className="contact-radiobutton"
              id="email"
              name="contact-preference"
              label={<FormattedMessage id={CONTACT_PREFERENCES.EMAIL} defaultMessage="Email" />}
              checked={contactType === CONTACT_PREFERENCES.EMAIL}
              handleChange={() => handleFilter(CONTACT_PREFERENCES.EMAIL)}
            />
          </li>
          <li key={CONTACT_PREFERENCES.PHONE}>
            <RadioButton
              className="contact-radiobutton"
              id="telephone"
              name="contact-preference"
              label={<FormattedMessage id={CONTACT_PREFERENCES.PHONE} defaultMessage="Telephone" />}
              checked={contactType === CONTACT_PREFERENCES.PHONE}
              handleChange={() => handleFilter(CONTACT_PREFERENCES.PHONE)}
            />
          </li>
        </ul>
        <TextField
          className="contact"
          label={<FormattedMessage id={fieldLabel} defaultMessage="Email / Telephone" />}
          id="reg_contact_value"
          size="large"
          type={contactType === CONTACT_PREFERENCES.EMAIL ? CONTACT_PREFERENCES.EMAIL : 'text'}
          value={contactValue}
          onChange={setContactValue}
        />
      </div>
    );
  }

  return (
    <div className="cp-event-registration-modal">
      {resendEmail ? (
        <>
          <ModalHeader
            title={<FormattedMessage id="events.registration.confirm" defaultMessage="Confirm your attendance" />}
          />
          <ModalContent>
            <FormattedHTMLMessage
              id="events.registration.confirm.message"
              defaultMessage="If you want to make a change to your attendance, please contact the library for help."
            />
          </ModalContent>
          <ModalFooter
            primaryActionButtonText={
              <FormattedMessage id="resend_confirmation_email" defaultMessage="Resend confirmation email" />
            }
            primaryActionButtonTag={TransactionalSolidButton}
            secondaryActionButtonText={<FormattedMessage defaultMessage="Close" id="button_close" />}
            handlePrimaryAction={handleResendConfirmationEmail}
            handleSecondaryAction={handleClose}
            dataKey="resend-email-confirmation"
          />
        </>
      ) : (
        <>
          <ModalHeader title={renderLabel()} />
          <ModalContent>
            <div className="event-details">
              <EventDateStamp event={event} />
              <div className="event-title">
                <div className="section-title">{event.getIn(['definition', 'title'])}</div>
                {renderEventInfo()}
              </div>
            </div>
            {scope && (
              <div className="series-info">
                <FormattedMessage
                  id="events.registration.series.message"
                  values={{ count: eventSeries.get('eventKeys')?.size, seriesStartDay, seriesStartDate }}
                  defaultMessage={`You will be registered for all ${
                    eventSeries.get('eventKeys')?.size
                  } events in this series, beginning ${seriesStartDate}.`}
                />
              </div>
            )}
            <div className="registration-form-name">
              {currentUser?.getIn(['userContact', 'firstName']) ? (
                <div className="user-name">
                  <label htmlFor="first-name">
                    <FormattedMessage id="first_name" defaultMessage="First name" />
                  </label>
                  <div id="first-name">{firstName}</div>
                </div>
              ) : (
                <TextField
                  className="name"
                  label={<FormattedMessage id="first_name" defaultMessage="First name" />}
                  id="reg_first_name"
                  size="large"
                  type="text"
                  value={firstName}
                  onChange={setFirstName}
                />
              )}
              {currentUser?.getIn(['userContact', 'lastName']) ? (
                <div className="user-name">
                  <label htmlFor="last-name">
                    <FormattedMessage id="last_name" defaultMessage="Last name" />
                  </label>
                  <div id="last-name">{lastName}</div>
                </div>
              ) : (
                <TextField
                  className="name"
                  label={<FormattedMessage id="last_name" defaultMessage="Last name" />}
                  id="reg_last_name"
                  size="large"
                  type="text"
                  value={lastName}
                  onChange={setLastName}
                />
              )}
            </div>
            <div className="registration-form-contact">{renderContact()}</div>
            <div className="registration-form-attendance">
              <div className="section-title">
                <FormattedMessage id="number_attending" defaultMessage="Number of people attending" />
              </div>
              <Select
                id="attendance-select"
                dataKey="attendance-select"
                name="attendance"
                value={attendance}
                handleChange={setAttendance}
                prompt=""
              >
                {attendanceOptions.map(i => (
                  <SelectOption key={`seat-${i}`} value={i}>
                    {i}
                  </SelectOption>
                ))}
              </Select>
            </div>
            {currentLibrary?.getIn(['events', 'eventsPrivacyDisclaimerEnabled']) && (
              <Link
                className="privacy-disclaimer"
                href={currentLibrary?.getIn(['events', 'eventsPrivacyDisclaimerUrl'])}
                dataKey="privacy-disclaimer-link"
                target="_blank"
              >
                <FormattedMessage
                  id="events_privacy_disclaimer"
                  defaultMessage="Learn how the library protects your privacy"
                />
              </Link>
            )}
          </ModalContent>
          <ModalFooter
            disabled={!firstName || !contactValue}
            primaryActionButtonText={renderLabel()}
            primaryActionButtonTag={TransactionalSolidButton}
            secondaryActionButtonText={<FormattedMessage defaultMessage="Cancel" id="button_cancel" />}
            handlePrimaryAction={handleEventRegistration}
            handleSecondaryAction={handleClose}
            dataKey="events-registration-modal"
          />
        </>
      )}
    </div>
  );
}

EventRegistrationModal.propTypes = {
  currentLibrary: libraryShape.isRequired,
  currentUser: userIdentityShape,
  event: eventShape.isRequired,
  eventSeries: ImmutablePropTypes.map.isRequired, // TODO: define eventSeries shape
  modalKey: PropTypes.string.isRequired,
  status: PropTypes.string,
  eventSettings: ImmutablePropTypes.mapContains({
    googleMapsOptions: ImmutablePropTypes.mapContains({
      apiKey: PropTypes.string.isRequired
    }),
    eventRegistrationOptions: ImmutablePropTypes.mapContains({
      providerExternal: PropTypes.bool.isRequired,
      providerBiblioEvents: PropTypes.bool.isRequired,
      maxSeats: PropTypes.number,
      emailRequiredForWaitlist: PropTypes.bool.isRequired
    })
  })
};
